import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import useStyles, { TRUSTPILOT_STAR_COLORS, TRUSTPILOT_EMPTY_STAR_COLOR } from './TrustpilotProductReviewStars.styles';
/**
 * Exact white star path from the Figma design (viewBox 0 0 12 11).
 * The background rect carries the rating colour; this path is always white.
 */
const STAR_PATH = 'M5.6875 8.08333L8.15885 7.46667L9.19141 10.6L5.6875 8.08333ZM11.375 4.03333H7.02474L5.6875 0L4.35026 4.03333H0L3.52083 6.53333L2.18359 10.5667L5.70443 8.06667L7.87109 6.53333L11.375 4.03333Z';
/** Half-star split at the horizontal centre of the 12-unit viewBox */
const HALF_X = 6;
const getStarColor = (avg) => {
    if (avg < 1.5)
        return TRUSTPILOT_STAR_COLORS.bad;
    if (avg < 2.5)
        return TRUSTPILOT_STAR_COLORS.poor;
    if (avg < 3.5)
        return TRUSTPILOT_STAR_COLORS.average;
    if (avg < 4.5)
        return TRUSTPILOT_STAR_COLORS.great;
    return TRUSTPILOT_STAR_COLORS.excellent;
};
/**
 * Rounds the average to the nearest 0.5 increment and determines
 * whether a star at a given index should be full, half, or empty.
 */
const getStarFill = (starIndex, avg) => {
    const rounded = Math.round(avg * 2) / 2;
    if (rounded >= starIndex + 1)
        return 'full';
    if (rounded >= starIndex + 0.5)
        return 'half';
    return 'empty';
};
const formatCount = (count) => count.toLocaleString('en-GB');
/**
 * Renders a single Trustpilot-style star tile.
 *
 * Design: coloured square background with a white star cutout on top.
 * - full  → entire background is the rating colour
 * - half  → left half is the rating colour, right half is the empty/grey colour
 * - empty → entire background is the empty/grey colour
 *
 * viewBox matches the Figma SVG asset (0 0 12 11).
 * Rendered at 17×16 — the aspect ratio (12:11 ≈ 1.09) scales cleanly to that size.
 */
const StarIcon = ({ fill, color }) => (_jsxs("svg", { width: "17", height: "16", viewBox: "0 0 12 11", fill: "none", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", role: "img", children: [fill === 'half' ? (_jsxs(_Fragment, { children: [_jsx("rect", { x: "0", y: "0", width: HALF_X, height: "11", fill: color }), _jsx("rect", { x: HALF_X, y: "0", width: HALF_X, height: "11", fill: TRUSTPILOT_EMPTY_STAR_COLOR })] })) : (_jsx("rect", { width: "12", height: "11", fill: fill === 'full' ? color : TRUSTPILOT_EMPTY_STAR_COLOR })), _jsx("path", { d: STAR_PATH, fill: "white" })] }));
const TrustpilotProductReviewStars = ({ avg, count, className, isPDPRedesign, isNewPDPCardReDesign }) => {
    const { classes } = useStyles();
    const clampedAvg = Math.max(0, Math.min(5, avg));
    const starColor = getStarColor(clampedAvg);
    const formattedCount = formatCount(count);
    return (_jsxs("div", { className: clsx(classes.wrapper, className, isNewPDPCardReDesign && classes.starContentWrapper), role: "img", "aria-label": `Rated ${clampedAvg} out of 5 stars based on ${formattedCount} reviews on Trustpilot`, children: [_jsx("div", { className: classes.starsContainer, children: Array.from({ length: 5 }, (_, i) => (_jsx(StarIcon, { fill: getStarFill(i, clampedAvg), color: starColor }, i))) }), _jsxs("span", { className: classes.count, children: [isPDPRedesign ? `${clampedAvg} ` : '', "(", _jsx("span", { className: classes.countValue, children: formattedCount }), ")"] })] }));
};
export default TrustpilotProductReviewStars;
